import { useId, useRef, useState } from "react";
import Link from "next/link";
import { Plus } from "lucide-react";

import { useTrack } from "@/analytics/events";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import Text from "@/blueprint/components/Text/Text";
import { cn } from "@/lib/utils";
import Section from "@/blueprint/components/Section/Section";
import {
  SHOP_FAQS,
  type ShopFaqGroup,
  type ShopFaqItem,
  shopFaqParagraphText,
} from "@/views/shop/shopContent";

type FaqItemProps = ShopFaqItem & {
  open: boolean;
  onToggle: () => void;
};

const FaqItem = ({ question, lead, answer, open, onToggle }: FaqItemProps) => {
  const answerId = useId();

  return (
    <div className="border-t border-stroke">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={answerId}
        onClick={onToggle}
        className="flex w-full items-center gap-xxl py-l text-left"
      >
        <Text variant="paragraph-md" as="span" className="min-w-0 flex-1">
          {question}
        </Text>
        <Plus
          aria-hidden
          className={cn(
            "size-xl shrink-0 transition-transform duration-quick ease-in-out-heavy motion-reduce:transition-none",
            open && "rotate-45",
          )}
        />
      </button>
      <div
        id={answerId}
        role="region"
        aria-label={question}
        aria-hidden={!open}
        className={cn(
          "grid transition-[grid-template-rows] duration-quick ease-in-out-heavy motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-xs pb-l pr-4xl tab:pr-5xl">
            {answer.map((paragraph, index) => {
              const paragraphLead =
                index === 0
                  ? lead
                  : typeof paragraph !== "string" && "lead" in paragraph
                    ? paragraph.lead
                    : null;
              return (
                <Text
                  key={shopFaqParagraphText(paragraph)}
                  variant="paragraph-md"
                  tone="secondary"
                >
                  {paragraphLead && (
                    <>
                      <span className="font-semibold">
                        {paragraphLead}
                      </span>{" "}
                    </>
                  )}
                  {typeof paragraph === "string" ? paragraph : paragraph.text}
                  {typeof paragraph !== "string" && "link" in paragraph && (
                    <>
                      {" "}
                      <Link
                        href={paragraph.link.href}
                        className="whitespace-nowrap text-content-primary underline underline-offset-2 hover:text-content-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-content-primary"
                      >
                        {paragraph.link.label}
                      </Link>
                    </>
                  )}
                </Text>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

type FaqGroupProps = ShopFaqGroup & {
  defaultExpanded: boolean;
};

// Per the Figma mobile FAQs (node 5642-5308), categories themselves collapse
// below sd — only the first starts expanded. Desktop keeps every category
// visible in the sidebar grid.
const FaqGroup = ({ id, title, items, defaultExpanded }: FaqGroupProps) => {
  const track = useTrack();
  const [openId, setOpenId] = useState<string | null>(null);
  // Mirrors openId synchronously: two clicks in one tick would otherwise both
  // read the pre-render value and report two opens.
  const openIdRef = useRef<string | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const itemsId = useId();

  return (
    <section className="w-full sd:grid sd:grid-cols-[minmax(260px,382px)_minmax(0,1fr)] sd:items-start sd:gap-grid-gutter d:grid-cols-[382px_minmax(0,1fr)]">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={itemsId}
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-center gap-l py-l text-left sd:hidden"
      >
        <Text variant="h6" as="h3" className="min-w-0 flex-1">
          {title}
        </Text>
        <Plus
          aria-hidden
          className={cn(
            "size-xl shrink-0 transition-transform duration-quick ease-in-out-heavy motion-reduce:transition-none",
            expanded && "rotate-45",
          )}
        />
      </button>
      <Text variant="h6" as="h3" className="hidden sd:block">
        {title}
      </Text>
      {/* Same collapse as FaqItem. Visibility rather than `hidden` so it can
          transition, and it still keeps a closed category off the tab order —
          breakpoint-aware in CSS, which an aria-hidden flag could not be. */}
      <div
        id={itemsId}
        className={cn(
          "grid transition-[grid-template-rows,visibility] duration-quick ease-in-out-heavy motion-reduce:transition-none",
          expanded ? "visible grid-rows-[1fr]" : "invisible grid-rows-[0fr]",
          "sd:visible sd:grid-rows-[1fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {items.map((item) => (
            <FaqItem
              key={item.id}
              {...item}
              open={openId === item.id}
              onToggle={() => {
                const isOpening = openIdRef.current !== item.id;
                openIdRef.current = isOpening ? item.id : null;
                setOpenId(openIdRef.current);
                if (isOpening) {
                  track("faq_opened", { question_id: item.id, group_id: id });
                }
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ShopFaqs = ({ position }: { position: number }) => (
  <Section
    id="faqs"
    position={position}
    elementId="faq"
    className="flex flex-col items-center gap-full px-grid-margin py-page"
  >
    <div className="flex flex-col items-center gap-s text-center">
      <Eyebrow variant="eyebrow-lg" className="sd:text-eyebrow-sm">
        {SHOP_FAQS.eyebrow}
      </Eyebrow>
      <Text variant="h2" as="h2" className="sd:text-h3">
        {SHOP_FAQS.title}
      </Text>
    </div>
    <div className="flex w-full max-w-[1330px] flex-col gap-full">
      {SHOP_FAQS.groups.map((group, index) => (
        <FaqGroup key={group.id} {...group} defaultExpanded={index === 0} />
      ))}
    </div>
  </Section>
);

export default ShopFaqs;
